import { useToast as useToastification } from 'vue-toastification'
import { getFirebaseErrorMessage } from '@/utils/firebase-errors'

/**
 * Composable for displaying toast notifications
 * Provides a consistent API for showing success, error, warning, and info messages
 */
export function useToast() {
  const toast = useToastification()

  /**
   * Show success toast
   * @param message - Success message to display
   */
  function success(message: string) {
    toast.success(message, {
      timeout: 4000,
    })
  }

  /**
   * Show error toast
   * @param error - Error object or string message
   * @param fallbackMessage - Optional fallback message if error parsing fails
   */
  function error(error: any, fallbackMessage?: string) {
    let message: string

    if (typeof error === 'string') {
      message = error
    } else {
      message = getFirebaseErrorMessage(error)
    }

    // Use fallback if provided and generic message was returned
    if (fallbackMessage && message === 'An unexpected error occurred. Please try again.') {
      message = fallbackMessage
    }

    toast.error(message, {
      timeout: 6000, // Longer timeout for errors to ensure users can read them
    })
  }

  /**
   * Show warning toast
   * @param message - Warning message to display
   */
  function warning(message: string) {
    toast.warning(message, {
      timeout: 5000,
    })
  }

  /**
   * Show info toast
   * @param message - Info message to display
   */
  function info(message: string) {
    toast.info(message, {
      timeout: 4000,
    })
  }

  /**
   * Show API error toast
   * Handles both Firebase and Axios errors
   * @param error - Error object from API call
   */
  function apiError(error: any) {
    let message = 'An error occurred. Please try again.'

    // Handle Axios errors
    if (error?.response?.data?.error) {
      message = error.response.data.error
    } else if (error?.response?.data?.message) {
      message = error.response.data.message
    } else if (error?.response?.statusText) {
      message = error.response.statusText
    } else {
      // Try Firebase error mapping
      message = getFirebaseErrorMessage(error)
    }

    toast.error(message, {
      timeout: 6000,
    })
  }

  /**
   * Clear all toasts
   */
  function clear() {
    toast.clear()
  }

  return {
    success,
    error,
    warning,
    info,
    apiError,
    clear,
  }
}
